import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import { ReportAdButton } from "@/components/ReportAdButton";
import { labelFor } from "@/lib/categories";
import { IMAGES } from "@/lib/images";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, MapPin, Calendar, Gift, Sparkles, PackageSearch,
  Facebook, MessageCircle, Link2, Flag, Image as ImageIcon,
  KeyRound, ShieldCheck, User, Mail, Phone, CheckCircle2, X, PackageCheck,
} from "lucide-react";
import { XIcon } from "@/components/XIcon";
import { InstagramIcon, TikTokIcon } from "@/components/BrandIcons";
import PhotoGallery from "@/components/PhotoGallery";
import ImageUpload from "@/components/ImageUpload";
import { FalseDeclarationWarning } from "@/components/FalseDeclarationWarning";
import FinderDeclarationDialog from "@/components/FinderDeclarationDialog";

// Server message (French) used to detect the duplicate-report 409.
const ALREADY_REPORTED_MSG = "Vous avez déjà signalé avoir trouvé cet objet. Votre déclaration est en cours de traitement.";
import { isAfrican } from "@/lib/countries";
import { CategoryAvatar } from "@/components/CategoryAvatar";
import { FindoSpinner, PageLoader } from "@/components/FindoSpinner";

const JSONLD_ID = "annonce-jsonld";

export default function AnnonceDetail() {
  const { adId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromNotif = searchParams.get("from") === "notif";
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { user } = useB2C();
  const [ad, setAd] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningTarget, setWarningTarget] = useState("contact");

  // Attribution obligatoire : connexion requise avant de signaler un objet.
  // Objet TROUVÉ : le propriétaire doit d'abord vérifier « est-ce bien mon objet ? »
  // avant d'ouvrir le formulaire de contact du trouveur (parité flux croisé).
  const requireLogin = () => {
    if (user) return true;
    toast.error(t("an.loginRequired"));
    navigate(`/mon-compte?next=${encodeURIComponent(`${window.location.pathname}?action=contact`)}`);
    return false;
  };

  // Not logged in → login (then back here). Logged in → « Attention » first, always.
// Back from login (?action=contact): resume on the clicked ad, warning first.
useEffect(() => {
if (user && ad?.flow_v2 && searchParams.get("resume_draft") === "1" && !ad.already_contacted) { setContactOpen(true); return; }
if (!user || !ad || searchParams.get("action") !== "contact") return;
setSearchParams({}, { replace: true });
openContact();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [user, ad]);
const openContact = () => {
setWarningTarget(ad?.ad_type === "found" && !ad?.flow_v2 ? "confirm" : "contact");
setWarningOpen(true);
};
const onWarningContinue = () => {
setWarningOpen(false);
if (!requireLogin()) return;
if (warningTarget === "confirm") setConfirmOpen(true);
else setContactOpen(true);
};

  useEffect(() => {
    let alive = true;
    const q = user?.email ? `?viewer_email=${encodeURIComponent(user.email)}` : "";
    api(`/public-ads/${adId}${q}`, { auth: false })
      .then((r) => { if (alive) setAd(r); })
      .catch(() => { if (alive) setNotFound(true); });
    // register a view (once per session)
    const key = `findo_ad_view_${adId}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      api(`/public-ads/${adId}/view`, { method: "POST", auth: false }).catch(() => {});
    }
    return () => { alive = false; };
  }, [adId, user, lang]);

  // Inject SEO title, meta description, Open Graph tags and JSON-LD.
  useEffect(() => {
    if (!ad) return;
    const title = `${labelFor(ad.category)} — ${(ad.description || "").slice(0, 60)} | Findloop`;
    const desc = `${labelFor(ad.category)} ${t("hc.lostIn")} ${ad.lost_place || ""}. ${(ad.description || "").slice(0, 120)}`;
    const shareImg = `${API}/public-ads/${ad.id}/share-image.png?v=${encodeURIComponent(ad.updated_at || ad.created_at || "")}`;
    const pageUrl = `${window.location.origin}/annonces/${ad.id}`;
    document.title = title;
    setMeta("description", desc);
    setMetaProp("og:type", "article");
    setMetaProp("og:title", title);
    setMetaProp("og:description", desc);
    setMetaProp("og:image", shareImg);
    setMetaProp("og:url", pageUrl);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", shareImg);
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: `${labelFor(ad.category)} — ${(ad.description || "").slice(0, 60)}`,
      description: ad.description,
      category: labelFor(ad.category),
      image: shareImg,
    };
    let el = document.getElementById(JSONLD_ID);
    if (!el) {
      el = document.createElement("script");
      el.id = JSONLD_ID;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(jsonld);
    return () => { const e = document.getElementById(JSONLD_ID); if (e) e.remove(); };
  }, [ad]);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="annonce-notfound">
        <PackageSearch size={40} className="mx-auto text-slate-300" />
        <p className="mt-4 text-slate-500">{t("an.none")}</p>
        <Link to="/annonces"><Button variant="outline" className="mt-6 rounded-full">{t("an.badge")}</Button></Link>
      </div>
    );
  }

  if (!ad) return <PageLoader label={t("c.loading")} className="max-w-2xl mx-auto py-32" />;

  return (
    <div className="bg-background min-h-[70vh]">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mt-6 grid md:grid-cols-2 gap-8 bg-white rounded-3xl border border-border card-shadow overflow-hidden" data-testid="annonce-detail">
          <div className="relative h-72 md:h-full bg-slate-100 min-h-[280px]">
            {(ad.image_base64 || ad.image_base64_2) ? (
              <PhotoGallery photos={[ad.image_base64, ad.image_base64_2]} alt={ad.description} fill testid="annonce-gallery" />
            ) : (
              <CategoryAvatar category={ad.category} iconSize={64} />
            )}
            {ad.boosted && (
              <span className="absolute top-4 left-4 inline-flex items-center gap-1 boost-shine rounded-full bg-findo-red text-white px-3 py-1 text-xs font-bold shadow" data-testid="ad-detail-boost-badge">
                <Sparkles size={13} /> {t("an.boosted")}
              </span>
            )}
          </div>

          <div className="p-6 md:p-8 flex flex-col">
            <p className="text-xs font-semibold text-findo-red uppercase tracking-wide">{labelFor(ad.category)}</p>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-findo-navy" data-testid="annonce-title">{ad.description}</h1>
            <div className="mt-5 space-y-2.5 text-sm text-slate-600">
              <p className="flex items-center gap-2"><MapPin size={16} className="text-slate-400" /> {t(ad.ad_type === "found" ? "anf.foundAt" : "an.lostAt")} {ad.lost_place || [ad.city, ad.country].filter(Boolean).join(", ") || "—"}</p>
              {ad.lost_date && <p className="flex items-center gap-2"><Calendar size={16} className="text-slate-400" /> {ad.lost_date}</p>}
            </div>

            {(() => {
              // Hide the "J'ai trouvé cet objet" / contact button once the object's
              // restitution is already engaged — for BOTH lost and found ads. For a
              // FOUND ad this notably covers the case where the owner has already PAID
              // the restitution fees (contacts_unlocked → match_resolved).
              // Opened from the bell notification: no « C'est mon objet » on found ads.
              if (fromNotif && ad.ad_type === "found" && !ad.restitution_in_progress) return null;
              const locked = ad.match_resolved || ad.owner_code_verified || ad.restitution_in_progress
                || (ad.ad_type !== "found" && ad.already_contacted);
              if (ad.ad_type !== "found" && ad.already_contacted && !ad.match_resolved && !ad.owner_code_verified && !ad.restitution_in_progress) {
                return (
                  <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800 flex items-start gap-2" data-testid="annonce-already-reported">
                    <CheckCircle2 size={17} className="mt-0.5 shrink-0" /> {t("fr.already")}
                  </div>
                );
              }
              return locked ? (
                <div className="mt-6 rounded-full font-semibold text-center py-2.5 bg-slate-100 text-slate-500" data-testid="annonce-locked-badge">
                  {ad.restitution_in_progress ? t("an.restitutionInProgress") : (ad.match_resolved || ad.owner_code_verified ? t("an.resolved") : t("an.requestInProgress"))}
                </div>
              ) : (
                <Button onClick={openContact} className="mt-6 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="annonce-contact-btn">
                  {t(ad.ad_type === "found" ? "anf.claim" : "an.contact")}
                </Button>
              );
            })()}

            {!ad.flow_v2 && <CodeUnlockSection ad={ad} />}

            <ShareButtons ad={ad} t={t} />
            <div className="mt-3">
              <ReportAdButton adId={ad.id} />
            </div>
          </div>
        </div>
      </div>

      <ConfirmObjectDialog
        ad={confirmOpen ? ad : null}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => { setConfirmOpen(false); setContactOpen(true); }}
        t={t}
      />
      <FalseDeclarationWarning
        found={ad?.ad_type === "found"}
        open={warningOpen}
        onCancel={() => setWarningOpen(false)}
        onContinue={onWarningContinue}
      />
      {ad.flow_v2 ? (
        <FinderDeclarationDialog ad={contactOpen ? ad : null} onClose={() => setContactOpen(false)} user={user}
          onSent={() => setAd((a) => ({ ...a, already_contacted: true }))} />
      ) : (
        <ContactDialog ad={contactOpen ? ad : null} onClose={() => setContactOpen(false)} t={t} user={user}
          onSent={() => setAd((a) => ({ ...a, already_contacted: true }))} />
      )}
    </div>
  );
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setMetaProp(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function CodeUnlockSection({ ad }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [owner, setOwner] = useState(null);

  async function verify(e) {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const res = await api(`/public-ads/${ad.id}/verify-code`, { method: "POST", auth: false, body: { code: code.trim() } });
      setOwner(res?.owner || null);
      toast.success(t("pub.ownerContactsUnlocked"));
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }

  if (owner) {
    return (
      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="annonce-owner-contacts">
        <p className="text-sm font-bold text-emerald-700 flex items-center gap-1.5"><ShieldCheck size={16} /> {t("pub.ownerContacts")}</p>
        <p className="text-slate-700 flex items-center gap-1.5 text-sm"><User size={13} /> {owner.name}</p>
        {owner.email && <p className="text-slate-700 flex items-center gap-1.5 text-sm"><Mail size={13} /> <a href={`mailto:${owner.email}`} className="hover:text-findo-navy underline">{owner.email}</a></p>}
        {owner.phone && <p className="text-slate-700 flex items-center gap-1.5 text-sm"><Phone size={13} /> <a href={`tel:${owner.phone}`} className="hover:text-findo-navy underline">{owner.phone}</a></p>}
      </div>
    );
  }

  // Code already validated (persisted) — the process cannot be re-triggered.
  if (ad.owner_code_verified) {
    return (
      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2" data-testid="annonce-code-validated">
        <ShieldCheck size={16} className="text-emerald-600" />
        <p className="text-sm font-semibold text-emerald-700">{t("pub.codeAlreadyValidated")}</p>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-findo-navy hover:underline" data-testid="annonce-have-code-btn">
          <KeyRound size={15} /> {t("pub.haveOwnerCode")}
        </button>
      ) : (
        <form onSubmit={verify} className="rounded-xl border border-findo-navy/20 bg-findo-navy/[0.04] p-4 space-y-2.5" data-testid="annonce-code-block">
          <p className="text-xs text-slate-600 flex items-center gap-1.5"><KeyRound size={14} className="text-findo-navy" /> {t("pub.enterCodeUnlockOwner")}</p>
          <div className="flex gap-2">
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={16} placeholder={t("pub.codeExample")} className="h-10 rounded-xl font-mono uppercase" data-testid="annonce-code-input" />
            <Button type="submit" disabled={busy || !code.trim()} className="rounded-xl font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="annonce-code-verify-btn">{busy ? <FindoSpinner size={16} /> : t("pub.validate")}</Button>
          </div>
        </form>
      )}
    </div>
  );
}

function ShareButtons({ ad, t }) {
  const [showPreview, setShowPreview] = useState(false);
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/s/${ad.id}`;
  const text = `${labelFor(ad.category)}: ${ad.description}`;
  const enc = encodeURIComponent;
  const links = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    twitter: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`,
    whatsapp: `https://wa.me/?text=${enc(text + " " + url)}`,
  };
  const open = (href) => window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  async function copy() {
    try { await navigator.clipboard.writeText(url); toast.success(t("an.linkCopied")); }
    catch { toast.error(t("an.copyFailed")); }
  }
  async function shareSocial(label) {
    if (navigator.share) {
      try { await navigator.share({ title: "findloop", text, url }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(url); toast.success(t("hc.linkCopiedShareOn").replace("{label}", label)); }
    catch { toast.error(t("an.copyFailed")); }
  }
  const btn = "h-9 w-9 grid place-items-center rounded-full border border-border text-slate-500 hover:text-findo-navy hover:border-findo-navy transition-colors";
  return (
    <div className="mt-5 border-t border-border pt-4" data-testid="annonce-share">
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400 font-medium mr-1">{t("mc.share")}</span>
        <button type="button" onClick={() => open(links.facebook)} className={btn} style={{ color: "#1877F2" }} title="Facebook" data-testid="share-facebook"><Facebook size={16} /></button>
        <button type="button" onClick={() => open(links.twitter)} className={btn} style={{ color: "#000000" }} title="X / Twitter" data-testid="share-twitter"><XIcon size={16} /></button>
        <button type="button" onClick={() => open(links.whatsapp)} className={btn} style={{ color: "#25D366" }} title="WhatsApp" data-testid="share-whatsapp"><MessageCircle size={16} /></button>
        <button type="button" onClick={() => shareSocial("Instagram")} className={btn} title="Instagram" data-testid="share-instagram"><InstagramIcon size={16} /></button>
        <button type="button" onClick={() => shareSocial("TikTok")} className={btn} title="TikTok" data-testid="share-tiktok"><TikTokIcon size={16} /></button>
        <button type="button" onClick={copy} className={btn} title={t("an.copyLink")} data-testid="share-copy"><Link2 size={16} /></button>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className={`${btn} ml-auto ${showPreview ? "text-findo-navy border-findo-navy" : ""}`}
          title={t("pub.sharePreview")} data-testid="share-preview-toggle">
          <ImageIcon size={16} />
        </button>
      </div>
      {showPreview && (
        <img
          src={`${API}/public-ads/${ad.id}/share-image.png?v=${encodeURIComponent(ad.updated_at || ad.created_at || "")}`}
          alt={t("pub.previewAlt")}
          className="mt-3 w-full rounded-xl border border-border animate-in fade-in duration-300"
          data-testid="share-preview-img" />
      )}
    </div>
  );
}

function ConfirmObjectDialog({ ad, onCancel, onConfirm, t }) {
  const [offerDismissed, setOfferDismissed] = useState(false);
  return (
    <Dialog open={!!ad} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent aria-describedby={undefined} data-testid="ad-confirm-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("hc.isThisReallyYourItem")}</DialogTitle></DialogHeader>
        {ad && (
          <div className="space-y-4">
            {isAfrican(ad.country) && !offerDismissed && (
              <div className="relative rounded-xl bg-emerald-50 border border-emerald-300 px-4 py-3 pr-11" data-testid="africa-launch-banner">
                <p className="text-sm font-bold text-emerald-900 flex items-center gap-2">
                  <Sparkles size={16} className="text-emerald-600 shrink-0" />
                  {t("hc.freeRecoveryAcrossAfricaLaunch")}
                </p>
                <button type="button" onClick={() => setOfferDismissed(true)} aria-label={t("hc.close")} title={t("hc.close")}
                  className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full bg-emerald-600 text-white ring-1 ring-emerald-700 hover:bg-emerald-700 transition-colors"
                  data-testid="africa-launch-banner-dismiss">
                  <X size={15} strokeWidth={2.5} />
                </button>
              </div>
            )}
            <p className="text-sm text-slate-500">{t("hc.checkTheDetailsBeforeContacting")}</p>
            {(ad.image_base64 || ad.image_base64_2) && (
              <div className="h-52 rounded-xl overflow-hidden bg-slate-100">
                <PhotoGallery photos={[ad.image_base64, ad.image_base64_2]} alt={ad.description} fill testid="confirm-gallery" />
              </div>
            )}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-findo-red uppercase tracking-wide">{labelFor(ad.category)}</p>
              <p className="font-semibold text-findo-navy" data-testid="confirm-description">{ad.description}</p>
              <p className="flex items-center gap-2 text-sm text-slate-600"><MapPin size={15} className="text-slate-400" /> {t("an.lostAt")} {ad.lost_place || "—"}</p>
              {ad.lost_date && <p className="flex items-center gap-2 text-sm text-slate-600"><Calendar size={15} className="text-slate-400" /> {ad.lost_date}</p>}
            </div>
            <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
              <Button onClick={onConfirm} className="flex-1 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90 gap-1.5" data-testid="confirm-object-yes">
                <CheckCircle2 size={17} /> {t("hc.yesContactTheFinder")}
              </Button>
              <Button onClick={onCancel} variant="outline" className="flex-1 rounded-full font-semibold" data-testid="confirm-object-no">
                {t("hc.thisIsNotMyItem")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ContactDialog({ ad, onClose, t, user, onSent }) {
  const { lang } = useLang();
  const [form, setForm] = useState({ finder_first_name: "", finder_last_name: "", finder_email: "", finder_phone: "", message: "", share_consent: false, photo: "" });
  const needsPhoto = ad?.ad_type !== "found";
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (ad && user) {
      setForm((f) => ({ ...f, finder_first_name: user.first_name || f.finder_first_name, finder_last_name: user.last_name || f.finder_last_name, finder_email: user.email || f.finder_email, finder_phone: user.phone || f.finder_phone }));
    }
  }, [ad, user]);
  async function submit(e) {
    e.preventDefault();
    if (!form.share_consent) { toast.error(t("an.shareConsentError")); return; }
    if (needsPhoto && !form.photo) { toast.error(t("fr.photoError")); return; }
    setBusy(true);
    try {
      const res = await api(`/public-ads/${ad.id}/contact`, { method: "POST", auth: false, body: { ...form, finder_first_name: form.finder_first_name.trim(), finder_last_name: form.finder_last_name.trim(), finder_name: `${form.finder_first_name.trim()} ${form.finder_last_name.trim()}`, finder_email: form.finder_email.trim().toLowerCase(), lang } });
      toast.success(res?.message || t("an.sent"));
      onSent?.();
      onClose();
      setForm({ finder_first_name: "", finder_last_name: "", finder_email: "", finder_phone: "", message: "", share_consent: false, photo: "" });
    } catch (err) {
      const dup = err.message === ALREADY_REPORTED_MSG;
      toast.error(dup ? t("fr.already") : err.message);
      if (dup) { onSent?.(); onClose(); }
    } finally { setBusy(false); }
  }
  return (
    <Dialog open={!!ad} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="ad-contact-dialog">
        <DialogHeader><DialogTitle icon={PackageCheck} className="font-display">{t("an.contactTitle")}</DialogTitle></DialogHeader>
        {ad && (
          <form onSubmit={submit} className="findo-numbered space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("hc.firstName")}</Label><Input required autoComplete="given-name" value={form.finder_first_name} onChange={(e) => setForm({ ...form, finder_first_name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="finder-first-name" /></div>
              <div><Label>{t("hc.lastName")}</Label><Input required autoComplete="family-name" value={form.finder_last_name} onChange={(e) => setForm({ ...form, finder_last_name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="finder-last-name" /></div>
            </div>
            <div><Label>{t("an.finderEmail")}</Label><Input type="email" required value={form.finder_email} onChange={(e) => setForm({ ...form, finder_email: e.target.value })} className="mt-1.5 rounded-xl" data-testid="finder-email" /></div>
            <div><Label>{t("an.finderPhone")}</Label><Input required value={form.finder_phone} onChange={(e) => setForm({ ...form, finder_phone: e.target.value })} className="mt-1.5 rounded-xl" data-testid="finder-phone" /></div>
            {needsPhoto && (
              <div data-testid="finder-photo-field">
                <Label>{t("fr.photoLabel")} <span className="text-findo-red">*</span></Label>
                <p className="text-xs text-slate-500 mt-0.5 mb-1.5">{t("fr.photoHint")}</p>
                <ImageUpload secure="finder" value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v }))} testid="finder-photo" />
              </div>
            )}
            <div><Label>{t("an.message")}</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-xl" data-testid="finder-message" /></div>
            <label className="flex items-start gap-2.5 rounded-xl bg-slate-50 border border-border p-3 cursor-pointer" data-testid="finder-consent-label">
              <input type="checkbox" checked={form.share_consent} onChange={(e) => setForm({ ...form, share_consent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-findo-navy" data-testid="finder-consent" />
              <span className="text-xs text-slate-600 leading-relaxed">{t("an.shareConsent")}</span>
            </label>
            <Button type="submit" disabled={busy || !form.share_consent || (needsPhoto && !form.photo)} className="w-full rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="finder-submit">
              {busy ? <FindoSpinner size={16} /> : t("an.send")}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
